import { Button, ButtonSizeVariant } from '@qr-front/ui/components/Button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogTitle,
} from '@qr-front/ui/components/Dialog'
import { PrimitiveAtom, useAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

type TSortDialogProps<T> = {
	title: string
	list: T[]
	isOpenAtom: PrimitiveAtom<boolean>
	renderItem: (
		item: T,
		index: number,
		onSortChange: (index: number, direction: 'up' | 'down') => void,
	) => React.ReactNode
	onSave?: (list: T[]) => void
	buttonSize?: ButtonSizeVariant
}

export const SortDialog = <T,>({
	title,
	list,
	isOpenAtom,
	renderItem,
	onSave,
	buttonSize = 'default',
}: TSortDialogProps<T>) => {
	const t = useTranslations()

	const [open, setOpen] = useAtom(isOpenAtom)
	const [tempList, setTempList] = useState<T[]>([])

	useEffect(() => {
		if (open) {
			setTempList([...list])
		}
	}, [open, list])

	const onOpenChange = () => setOpen((prev) => !prev)

	const onSortChange = (index: number, direction: 'up' | 'down') => {
		setTempList((prev) => {
			const newIndex = direction === 'up' ? index - 1 : index + 1

			if (newIndex < 0 || newIndex >= prev.length) return prev

			const updated = [...prev]
			const [moved] = updated.splice(index, 1)
			updated.splice(newIndex, 0, moved as T)

			return updated
		})
	}

	const handleSave = () => {
		onSave?.(tempList)
		setOpen(false)
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				className="flex w-[350px] flex-col gap-6 p-8 tablet:w-[500px]"
			>
				<DialogTitle className="font-semibold">{title}</DialogTitle>

				<ul className="my-6 flex flex-col gap-2">
					{tempList.map((item, index) => renderItem(item, index, onSortChange))}
				</ul>

				<DialogFooter>
					<Button
						onClick={() => setOpen(false)}
						className="h-12 w-full"
						visual="secondary"
						size={buttonSize}
					>
						{t('ui__generic__cancel')}
					</Button>
					<Button
						onClick={handleSave}
						className="h-12 w-full"
						size={buttonSize}
					>
						{t('ui__generic__save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

SortDialog.displayName = 'SortDialog'
